import React, { useEffect, useMemo, useState } from "react";

import moment from "moment";
import { toast } from "react-toastify";

import ReportSearch from "../../../../Product/Report/ReportSearch";
import { useAccessStatus } from "../../../../grace-period/hooks/useAccessStatus";
import PdfPreviewModal from "../../../UIComponents/PdfPreviewModal";
import StyledScrollbar from "../../../UIComponents/StyledScrollbar";
import { createAndAppendIdentifierList } from "../../helper";
import useTemplateAsyncAction from "../../hooks/useTemplateAsyncAction";
import { useTemplateContext } from "../../TemplateContext";
import CreateReportButton from "../Common/CreateReportButton";
import NoResultFound from "../Common/NoResultFound";
import DataFieldsButton from "../DataFieldsButton";
import COADeleteModal from "./COADeleteModal";
import COAListItem from "./COAListItem";

import { getFileFromS3WithPresigned, downloadFileFromS3WithPresigned } from "../../../../../utils/helpers";

import {
  manageCoa,
  getCoas,
} from "../../../../../actions/productTemplate";

import { ReactComponent as RectangleLine } from "../../../../../assets/images/environment/RectangleLine.svg";

import "./COAList.css";
import "../Common/COALoadingPage.css";

export default function COAList(props) {
  const {
    handleAddCoa,
    coaList,
    setCoaList,
    linkReportKeys,
    readOnlyTemplateIds,
    showDataFieldsModal,
    toggleDataFieldsModal,
  } = props;
  const { withSide } = useTemplateContext();
  const { areResultsLocked } = useAccessStatus();
  const [pdfObjForPreview, setPdfObjForPreivew] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [elemToBeDeleted, setElemToBeDeleted] = useState(null);
  const [searchedValue, setSearchedValue] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const { loading: searchLoading, run: runSearchAction } = useTemplateAsyncAction();
  const { loading: deleteLoading, run: runDeleteAction } = useTemplateAsyncAction();
  const { loadingKey: previewLoadingKey, run: runPreviewAction, abort: abortPreviewAction } = useTemplateAsyncAction();

  useEffect(() => () => abortPreviewAction(), [abortPreviewAction]);

  const refreshCoaList = async () => {
    const params = withSide({ category: "fetchList" });
    const response = await getCoas(params);
    if (response && response.message === "Success") {
      const result = createAndAppendIdentifierList(response, linkReportKeys);
      setCoaList(result);
    } else {
      toast.error("Failed to get COA list.");
      setCoaList([]);
    }
    return response;
  };

  const apiDeleteCoa = () => runDeleteAction(async () => {
    if (!elemToBeDeleted) {
      toast.error("Invalid COA.");
      return undefined;
    }
    const params = withSide({
      coa_id: elemToBeDeleted.coa_id, action: "Delete", method: "PATCH",
    });
    const response = await manageCoa(params);
    if (response && response.message === "Success") {
      await refreshCoaList();
      setElemToBeDeleted(null);
      setShowDeleteModal(false);
      toast.success("Successfully deleted COA.");
    } else {
      toast.error("Failed to delete COA.");
    }
    return response;
  });

  const apiSearchCoas = (searchValue) => runSearchAction(async () => {
    const params = withSide({ search: searchValue, category: "Search" });
    const response = await getCoas(params);

    if (response && response.success) {
      const result = createAndAppendIdentifierList(response, linkReportKeys);
      setSearchedValue(searchValue);
      setCoaList(result);
    } else {
      setSearchedValue("");
      toast.error("Failed to search COAs.");
    }
    return response;
  });

  const handleItemPreview = (elem) => runPreviewAction(async (signal) => {
    if (areResultsLocked) {
      return undefined;
    }
    if (pdfObjForPreview?.url) {
      URL.revokeObjectURL(pdfObjForPreview.url);
      setPdfObjForPreivew(null);
    }
    const fileBlobObj = await getFileFromS3WithPresigned([elem.pdf_path], "private", signal);
    if (signal?.aborted) {
      return undefined;
    }
    if (!fileBlobObj) {
      toast.error("Failed to preview");
      return undefined;
    }
    const blob = fileBlobObj[elem.pdf_path];
    if (!blob) {
      toast.error("Failed to preview");
      return undefined;
    }
    const url = window.URL.createObjectURL(blob);
    if (url) {
      setPdfObjForPreivew({
        url,
        key: elem.coa_id || elem.pdf_path,
        fileName: elem.pdf_path.split("/").pop(),
        title: elem.coa_name || "COA",
      });
    } else {
      toast.error("Failed to preview");
    }
    return url;
  }, { useAbort: true, preventConcurrent: false, key: elem.coa_id });

  const handleItemDownload = async (elem) => {
    if (areResultsLocked) {
      return false;
    }
    const fileName = elem.pdf_path.split("/").pop();
    const downloadSuccess = await downloadFileFromS3WithPresigned([elem.pdf_path], "private", [fileName]);
    if (!downloadSuccess) {
      toast.error("Failed to download");
    }
    return downloadSuccess;
  };

  const handleFilterByDate = (dateStart, dateEnd) => {
    setFilterDateFrom(dateStart);
    setFilterDateTo(dateEnd);
  };

  const getCoaDates = async () => {
    const dates = coaList.map((coa) => moment(coa.added_on).format("MM/DD/YYYY"));
    return [...new Set(dates)];
  };

  const filteredCoaList = useMemo(() => {
    if (!filterDateFrom && !filterDateTo) {
      return coaList;
    }
    return coaList.filter((coa) => {
      const coaDate = moment(coa.added_on).startOf("day");
      const from = filterDateFrom ? moment(filterDateFrom, "YYYY-MM-DD").startOf("day") : null;
      const to = filterDateTo ? moment(filterDateTo, "YYYY-MM-DD").startOf("day") : null;
      if (from && to) {
        return coaDate.isSameOrAfter(from) && coaDate.isSameOrBefore(to);
      }
      if (from) {
        return coaDate.isSame(from);
      }
      if (to) {
        return coaDate.isSame(to);
      }
      return true;
    });
  }, [coaList, filterDateFrom, filterDateTo]);

  const toggleShowDeleteModal = (elem) => {
    if (elem) {
      setElemToBeDeleted(elem);
    }
    setShowDeleteModal(!showDeleteModal);
  };

  const toggleShowCoaPreview = () => {
    if (pdfObjForPreview?.url) {
      URL.revokeObjectURL(pdfObjForPreview.url);
    }
    setPdfObjForPreivew(null);
  };

  return (
    <div className="coaListContainer">
      <div className="coaListToolbar">
        <div className="coaListToolbar__Title">
          <RectangleLine aria-hidden />
          <span>Create custom COA&apos;s</span>
        </div>
        <div className="coaListToolbar__Search CoaReportSearch">
          <ReportSearch
            flag="coa_reports"
            placeholder="Search..."
            handleSearch={apiSearchCoas}
            handleFilterByDate={handleFilterByDate}
            getReportDates={getCoaDates}
          />
        </div>
        <div className="coaListToolbar__Actions">
          <CreateReportButton
            handleAddCoa={handleAddCoa}
            buttonText="Create Custom Report"
            showAddIcon
          />
          <DataFieldsButton
            toggleDataFieldsModal={toggleDataFieldsModal}
            showDataFieldsModal={showDataFieldsModal}
            inToolbar
          />
        </div>
      </div>
      {searchLoading ? (
        <div className="COAList__SearchSkeleton">
          <div className="COALoading ActiveloadingAnimation" />
        </div>
      ) : (
        filteredCoaList.length === 0
          ? <div className="COAList__NoResultFound"><NoResultFound /></div>
          : (
            <StyledScrollbar
              className="COAList__ScrollBar"
              id="coaListContainerScrollbar"
            >
              <div className="coaListItems">
                {filteredCoaList.map((elem, index) => (
                  <COAListItem
                    key={index}
                    elem={elem}
                    toggleShowDeleteModal={toggleShowDeleteModal}
                    onPreview={() => handleItemPreview(elem)}
                    onDownload={() => handleItemDownload(elem)}
                    previewBusy={previewLoadingKey === elem.coa_id}
                    searchedValue={searchedValue}
                    readOnlyTemplateIds={readOnlyTemplateIds}
                  />
                ))}
              </div>
            </StyledScrollbar>
          )
      )}
      {showDeleteModal && (
        <COADeleteModal
          toggleShowDeleteModal={toggleShowDeleteModal}
          apiDeleteCoa={apiDeleteCoa}
          deleteLoading={deleteLoading}
        />
      )}
      {pdfObjForPreview && (
        <PdfPreviewModal
          open
          title={pdfObjForPreview.title}
          onClose={toggleShowCoaPreview}
          files={[{
            key: pdfObjForPreview.key,
            fileName: pdfObjForPreview.fileName,
            src: pdfObjForPreview.url,
          }]}
          errorMessage="Unable to preview this COA."
          width="40vw"
        />
      )}
    </div>
  );
}
